import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";

import PageHeader from "./colour-families/PageHeader";
import FamilyGrid from "./colour-families/FamilyGrid";
import SwatchesView from "./colour-families/SwatchesView";
import OrderSummaryPanel from "./colour-families/OrderSummaryPanel";
import OrderSuccess from "./colour-families/OrderSuccess";
import CompareTray from "./colour-families/CompareTray";
import { qualities, calcCartTotals } from "./colour-families/helpers";
import { useColourCart } from "./colour-families/useColourCart";
import { usePinnedShades, pinKey } from "./colour-families/usePinnedShades";
import useIsCompact from "@/hooks/useIsCompact";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ColourFamiliesPage({ user, authed }) {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const initialQuality = qualities.find((q) => q.id === searchParams.get("quality"))?.id || "600-lining";

  const [families, setFamilies] = useState([]);
  const [selectedFamily, setSelectedFamily] = useState(null);
  const [selectedQuality, setSelectedQuality] = useState(initialQuality);
  const [orderSent, setOrderSent] = useState(false);
  const [sending, setSending] = useState(false);
  const [showSummary, setShowSummary] = useState(false);
  const [trayOpen, setTrayOpen] = useState(true);
  const [sharing, setSharing] = useState(false);
  const [shareCard, setShareCard] = useState(null);
  const [shareError, setShareError] = useState("");
  const isCompact = useIsCompact();

  const { cart, addToCart, addItems, updateQty, removeFromCart, clearCart } = useColourCart();
  const { pinned, togglePin, unpin, clearPins } = usePinnedShades();
  const pinnedKeys = new Set(pinned.map((p) => p.key));
  const currentQuality = qualities.find((q) => q.id === selectedQuality);
  const totals = calcCartTotals(cart);

  const handlePin = (num, hex) => {
    togglePin({
      key: pinKey(num, selectedQuality),
      colour_number: num,
      colour_name: `${selectedFamily.name} #${num}`,
      colour_family: selectedFamily.name,
      colour_hex: hex,
      fabric_quality: selectedQuality,
      fabric_name: currentQuality.name,
      panna: currentQuality.panna,
      meters_per_bundle: currentQuality.meters_per_bundle,
    });
    setTrayOpen(true);
  };

  const addPinnedToCart = () => {
    addItems(pinned);
    setShowSummary(true);
  };

  const shareShortlist = async () => {
    setSharing(true);
    setShareError("");
    try {
      const res = await fetch(`${API}/shortlist/card`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          items: pinned.map((p) => ({
            colour_number: p.colour_number,
            colour_family: p.colour_family,
            colour_hex: p.colour_hex,
            fabric_name: p.fabric_name,
          })),
        }),
      });
      if (!res.ok) throw new Error("card failed");
      const data = await res.json();
      const url = `${process.env.REACT_APP_BACKEND_URL}${data.image_url}`;
      const text = data.share_text.replace("{url}", url);
      setShareCard({ url, text });

      // On phones, share the image itself straight into WhatsApp
      if (navigator.canShare) {
        try {
          const blob = await (await fetch(url)).blob();
          const file = new File([blob], "mutha-paras-shade-shortlist.png", { type: "image/png" });
          if (navigator.canShare({ files: [file] })) {
            await navigator.share({ files: [file], text });
            return;
          }
        } catch (e) {
          // native share unavailable — fall through to the chat link
          console.debug("Native share unavailable, using wa.me link:", e);
        }
      }
      window.open(`https://wa.me/917624853840?text=${encodeURIComponent(text)}`, "_blank");
    } catch (err) {
      console.debug("Shortlist card failed:", err);
      setShareError("Could not build the card just now. Please try again.");
    } finally {
      setSharing(false);
    }
  };

  useEffect(() => {
    fetch(`${API}/colour-families`)
      .then((r) => r.json())
      .then(setFamilies)
      .catch(console.error);
  }, []);

  const handleQualityChange = (qId) => {
    setSelectedQuality(qId);
    setSearchParams({ quality: qId }, { replace: true });
  };

  const handleAddToCart = (num) => addToCart(selectedFamily, num, currentQuality);

  const submitOrder = async () => {
    if (!authed) {
      navigate("/login");
      return;
    }
    setSending(true);
    try {
      const items = cart.map((c) => ({
        colour_number: c.colour_number,
        colour_name: c.colour_name,
        colour_family: c.colour_family,
        fabric_quality: c.fabric_quality,
        quantity_meters: c.quantity_meters,
      }));
      const res = await fetch(`${API}/orders`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          items,
          notes: `Total: ${totals.totalMeters}m, ${totals.totalBundles} bundles`,
        }),
      });
      if (res.ok) {
        setOrderSent(true);
        clearCart();
        setShowSummary(false);
      }
    } catch (err) {
      console.error("Order failed:", err);
    } finally {
      setSending(false);
    }
  };

  const sendToWhatsApp = () => {
    const lines = cart.map(
      (c) =>
        `• #${c.colour_number} ${c.colour_family} (${c.fabric_name}) - ${c.quantity_meters}m (${Math.ceil(
          c.quantity_meters / c.meters_per_bundle
        )} bundles)`
    );
    const text = `Hi Mutha Paras & Co.,\n\nI'd like to order:\n\n${lines.join(
      "\n"
    )}\n\n📦 Total: ${totals.totalMeters} meters (${totals.totalBundles} bundles)\n🚛 Transport: Rs. 100/parcel (local lorry) or Rs. 150/parcel (bus & travels)\n📋 GST: 5% extra on fabric value\n\nPlease confirm availability, parcel count & total amount.`;
    window.open(`https://wa.me/917624853840?text=${encodeURIComponent(text)}`, "_blank");
  };

  const sendToInstagram = () =>
    window.open("https://instagram.com/theliningstoreindia", "_blank");

  const showPanel = cart.length > 0 && (showSummary || selectedFamily);

  return (
    <div
      className="min-h-screen"
      style={{ backgroundColor: "var(--mp-paper)" }}
      data-testid="colour-families-page"
    >
      <PageHeader
        selectedFamily={selectedFamily}
        cartCount={cart.length}
        pinCount={pinned.length}
        onBack={() => setSelectedFamily(null)}
        onToggleSummary={() => setShowSummary((v) => !v)}
        onToggleTray={() => setTrayOpen((v) => !v)}
      />

      <div className="max-w-7xl mx-auto px-6 lg:px-10 py-8" style={{ paddingBottom: pinned.length ? (trayOpen ? (shareCard ? 560 : 420) : 120) : undefined }}>
        {orderSent && <OrderSuccess onContinue={() => setOrderSent(false)} />}

        {!orderSent && !selectedFamily && (
          <FamilyGrid families={families} onSelect={setSelectedFamily} currentQualityName={currentQuality?.name} />
        )}

        {!orderSent && selectedFamily && (
          <SwatchesView
            selectedFamily={selectedFamily}
            selectedQuality={selectedQuality}
            onQualityChange={handleQualityChange}
            cart={cart}
            onAddToCart={handleAddToCart}
            pinnedKeys={pinnedKeys}
            onPin={handlePin}
          />
        )}

        {showPanel && (
          <OrderSummaryPanel
            cart={cart}
            totals={totals}
            token={authed}
            sending={sending}
            selectedFamily={selectedFamily}
            onUpdateQty={updateQty}
            onRemove={removeFromCart}
            onSubmit={submitOrder}
            onWhatsApp={sendToWhatsApp}
            onInstagram={sendToInstagram}
            onHide={() => setShowSummary(false)}
            bottomOffset={pinned.length ? (trayOpen ? (shareCard ? 476 : 356) : 84) : 0}
            sticky={!(isCompact && pinned.length > 0 && trayOpen)}
          />
        )}
      </div>

      <CompareTray
        pinned={pinned}
        open={trayOpen}
        onToggleOpen={() => setTrayOpen((v) => !v)}
        onRemove={unpin}
        onClear={() => {
          clearPins();
          setShareCard(null);
        }}
        onAddAll={addPinnedToCart}
        onShare={shareShortlist}
        sharing={sharing}
        shareCard={shareCard}
        shareError={shareError}
      />
    </div>
  );
}
